// src/pages/lms/LMSStaffLogin.jsx — COMPLETE FINAL
// Features:
// 1. Staff selects their institution from dropdown (fetched live)
// 2. Enters username → fetched via RPC with correct column staff_username
// 3. Enters PIN → verified via RPC
// 4. Session saved to localStorage
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const STAFF_KEY = 'lms_staff_session';
const INST_KEY  = 'lms_institution_id';

export default function LMSStaffLogin() {
  const navigate = useNavigate();

  const [step,       setStep]       = useState('institution'); // institution | username | pin
  const [institutions, setInstitutions] = useState([]);
  const [instId,     setInstId]     = useState('');
  const [instName,   setInstName]   = useState('');
  const [username,   setUsername]   = useState('');
  const [pin,        setPin]        = useState('');
  const [roleInfo,   setRoleInfo]   = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [instLoading,setInstLoading]= useState(true);
  const [error,      setError]      = useState('');

  const usernameRef = useRef();
  const pinRef      = useRef();

  // ── Load all institutions on mount ────────────────
  useEffect(() => {
    loadInstitutions();
  }, []);

  const loadInstitutions = async () => {
    setInstLoading(true);
    try {
      // Check if already logged in as staff
      // BUT: if user came here intentionally (from login page button),
      // we should let them login as a different staff member
      // Only auto-redirect if they navigated here directly with no intent to switch
      const urlParams = new URLSearchParams(window.location.search);
      const forceNew  = urlParams.get('switch') === '1';
      if (!forceNew) {
        try {
          const s = localStorage.getItem(STAFF_KEY);
          if (s) {
            const sess = JSON.parse(s);
            if (sess?.staff_id && sess?.institution_id) {
              navigate('/lms/dashboard', { replace: true });
              return;
            }
          }
        } catch(_) {}
      } else {
        // Clear old session so they can login fresh
        localStorage.removeItem(STAFF_KEY);
      }

      // Fetch all institutions
      const { data, error: e } = await supabase
        .from('institutions')
        .select('id, name')
        .order('name');

      if (e) throw e;
      const list = data || [];
      setInstitutions(list);

      // Auto-select if only one institution
      if (list.length === 1) {
        setInstId(list[0].id);
        setInstName(list[0].name);
        setStep('username');
        setTimeout(() => usernameRef.current?.focus(), 150);
        return;
      }

      // Try to pre-select from localStorage
      const stored = localStorage.getItem(INST_KEY);
      if (stored && list.find(i => i.id === stored)) {
        setInstId(stored);
        setInstName(list.find(i => i.id === stored)?.name || '');
      }
    } catch(e) {
      console.error('Load institutions error:', e);
    } finally {
      setInstLoading(false);
    }
  };

  const selectInstitution = () => {
    if (!instId) { setError('Please select your institution'); return; }
    localStorage.setItem(INST_KEY, instId);
    setError('');
    setStep('username');
    setTimeout(() => usernameRef.current?.focus(), 150);
  };

  // ── Step 2: Username lookup ───────────────────────
  const lookupUsername = async () => {
    const u = username.trim();
    if (!u) { setError('Enter your username'); return; }
    setLoading(true); setError('');
    try {
      const { data, error: e } = await supabase.rpc('lookup_staff_username', {
        p_institution_id: instId,
        p_username:       u.toLowerCase(),
      });
      if (e) throw e;
      if (!data?.found) {
        setError(data?.error || 'Username not found. Check with your admin.');
        setLoading(false); return;
      }
      setRoleInfo({
        staff_name: data.staff_name,
        role_name:  data.role_name  || 'Staff',
        role_emoji: data.role_emoji || '👤',
      });
      setStep('pin');
      setTimeout(() => pinRef.current?.focus(), 100);
    } catch(e) {
      setError(e.message || 'Lookup failed. Try again.');
    } finally { setLoading(false); }
  };

  // ── Step 3: PIN verification ──────────────────────
  const verifyPin = async () => {
    const p = pin.trim();
    if (!p) { setError('Enter your PIN'); return; }
    setLoading(true); setError('');
    try {
      const { data, error: e } = await supabase.rpc('verify_staff_login', {
        p_institution_id: instId,
        p_username:       username.trim().toLowerCase(),
        p_pin:            p,
      });
      if (e) throw e;
      if (!data?.success) {
        setError(data?.error || 'Incorrect PIN. Try again.');
        setPin('');
        setLoading(false);
        setTimeout(() => pinRef.current?.focus(), 50);
        return;
      }
      // Save session
      localStorage.setItem(STAFF_KEY, JSON.stringify({
        ...data,
        institution_id: instId,
        logged_in_at:   new Date().toISOString(),
      }));
      navigate('/lms/dashboard', { replace: true });
    } catch(e) {
      setError(e.message || 'Login failed. Try again.');
    } finally { setLoading(false); }
  };

  const INP = {
    width: '100%', padding: '13px 16px', borderRadius: 12,
    border: '1.5px solid #E5E7EB', fontSize: 15, outline: 'none',
    color: '#111827', boxSizing: 'border-box', background: '#fff',
    fontFamily: 'inherit',
  };

  if (instLoading) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'linear-gradient(135deg,#F1F5F9,#EFF6FF)' }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ width:44, height:44, border:'4px solid #E5E7EB', borderTopColor:'#4F8EF7', borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto 12px' }}/>
        <div style={{ color:'#6B7280', fontSize:13 }}>Loading institutions...</div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    </div>
  );

  return (
    <div style={{
      minHeight:'100vh',
      background:'linear-gradient(135deg,#F1F5F9 0%,#EFF6FF 50%,#F5F3FF 100%)',
      display:'flex', alignItems:'center', justifyContent:'center', padding:20,
    }}>
      <div style={{ width:'100%', maxWidth:440 }}>

        {/* Logo */}
        <div style={{ textAlign:'center', marginBottom:28 }}>
          <div style={{
            width:68, height:68, borderRadius:22, margin:'0 auto 14px',
            background:'linear-gradient(135deg,#4F8EF7,#7C3AED)',
            display:'flex', alignItems:'center', justifyContent:'center',
            boxShadow:'0 10px 30px rgba(79,142,247,0.3)',
          }}>
            <span style={{ fontSize:32 }}>🏫</span>
          </div>
          <div style={{ fontWeight:900, color:'#111827', fontSize:24 }}>
            {step === 'institution' ? 'UNIVERSE LMS' : instName}
          </div>
          <div style={{ color:'#6B7280', fontSize:13, marginTop:4 }}>Staff Portal</div>
        </div>

        {/* Progress dots */}
        <div style={{ display:'flex', justifyContent:'center', gap:8, marginBottom:24 }}>
          {['institution','username','pin'].map((s, i) => {
            const steps = ['institution','username','pin'];
            const curr  = steps.indexOf(step);
            const done  = i < curr;
            const active= i === curr;
            return (
              <div key={s} style={{
                width: active ? 24 : 8, height:8, borderRadius:4,
                background: done ? '#10B981' : active ? '#4F8EF7' : '#E5E7EB',
                transition:'all 0.3s',
              }}/>
            );
          })}
        </div>

        {/* Card */}
        <div style={{
          background:'#fff', borderRadius:24, padding:'32px 28px',
          boxShadow:'0 8px 40px rgba(0,0,0,0.08)', border:'1px solid #E5E7EB',
        }}>

          {error && (
            <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:10, padding:'11px 14px', marginBottom:20, fontSize:13, color:'#991B1B', fontWeight:600 }}>
              ❌ {error}
            </div>
          )}

          {/* ── STEP 1: SELECT INSTITUTION ── */}
          {step === 'institution' && (
            <>
              <h2 style={{ margin:'0 0 6px', fontWeight:800, color:'#111827', fontSize:20 }}>Select Your Institution</h2>
              <p style={{ color:'#6B7280', fontSize:13, margin:'0 0 24px' }}>Choose the institution you belong to</p>

              <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:8 }}>Institution</label>
              <select
                value={instId}
                onChange={e => {
                  setInstId(e.target.value);
                  setInstName(institutions.find(i => i.id === e.target.value)?.name || '');
                  setError('');
                }}
                style={{ ...INP, cursor:'pointer' }}
              >
                <option value="">— Select your institution —</option>
                {institutions.map(inst => (
                  <option key={inst.id} value={inst.id}>{inst.name}</option>
                ))}
              </select>

              {institutions.length === 0 && (
                <div style={{ marginTop:12, fontSize:12, color:'#F59E0B', fontWeight:600 }}>
                  ⚠️ No institutions found. Contact your admin.
                </div>
              )}

              <button
                onClick={selectInstitution}
                disabled={!instId}
                style={{
                  width:'100%', marginTop:20, padding:'13px 0', borderRadius:12, border:'none',
                  background: instId ? 'linear-gradient(135deg,#4F8EF7,#7C3AED)' : '#E5E7EB',
                  color: instId ? '#fff' : '#9CA3AF',
                  fontWeight:800, fontSize:15, cursor: instId ? 'pointer' : 'not-allowed',
                }}>
                Continue →
              </button>

              <div style={{ textAlign:'center', marginTop:20 }}>
                <a href="/lms" style={{ fontSize:12, color:'#9CA3AF', textDecoration:'none' }}>← Back to Admin Login</a>
              </div>
            </>
          )}

          {/* ── STEP 2: USERNAME ── */}
          {step === 'username' && (
            <>
              {/* Institution badge */}
              <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:'#F5F3FF', border:'1px solid #DDD6FE', borderRadius:12, marginBottom:20 }}>
                <span style={{ fontSize:20 }}>🏫</span>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:800, color:'#111827' }}>{instName}</div>
                  <div style={{ fontSize:11, color:'#7C3AED', fontWeight:600 }}>Institution selected</div>
                </div>
                <button onClick={() => { setStep('institution'); setError(''); }}
                  style={{ background:'none', border:'1px solid #DDD6FE', borderRadius:8, padding:'3px 8px', cursor:'pointer', color:'#7C3AED', fontSize:11, fontWeight:700 }}>
                  Change
                </button>
              </div>

              <h2 style={{ margin:'0 0 6px', fontWeight:800, color:'#111827', fontSize:20 }}>Enter Username</h2>
              <p style={{ color:'#6B7280', fontSize:13, margin:'0 0 24px' }}>The username given to you by your admin</p>

              <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:8 }}>Username</label>
              <input
                ref={usernameRef}
                value={username}
                onChange={e => setUsername(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && lookupUsername()}
                placeholder="e.g. ahmed_teacher"
                style={INP}
                autoComplete="username"
              />

              <button
                onClick={lookupUsername}
                disabled={loading}
                style={{
                  width:'100%', marginTop:16, padding:'13px 0', borderRadius:12, border:'none',
                  background: loading ? '#E5E7EB' : 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
                  color: loading ? '#9CA3AF' : '#fff',
                  fontWeight:800, fontSize:15, cursor: loading ? 'wait' : 'pointer',
                }}>
                {loading ? 'Checking...' : 'Continue →'}
              </button>

              <button onClick={() => { setStep('institution'); setError(''); }}
                style={{ width:'100%', marginTop:10, padding:'11px 0', borderRadius:12, border:'1.5px solid #E5E7EB', background:'#fff', color:'#374151', fontWeight:600, fontSize:14, cursor:'pointer' }}>
                ← Back
              </button>
            </>
          )}

          {/* ── STEP 3: PIN ── */}
          {step === 'pin' && roleInfo && (
            <>
              {/* Role badge */}
              <div style={{
                background:'linear-gradient(135deg,#F5F3FF,#EFF6FF)',
                border:'1px solid #DDD6FE', borderRadius:16, padding:16, marginBottom:24,
                display:'flex', alignItems:'center', gap:14,
              }}>
                <div style={{
                  width:54, height:54, borderRadius:14, flexShrink:0,
                  background:'linear-gradient(135deg,#7C3AED,#4F8EF7)',
                  display:'flex', alignItems:'center', justifyContent:'center', fontSize:26,
                }}>
                  {roleInfo.role_emoji}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:800, color:'#111827', fontSize:16 }}>{roleInfo.staff_name}</div>
                  <div style={{ fontSize:12, color:'#7C3AED', fontWeight:700, marginTop:2 }}>{roleInfo.role_emoji} {roleInfo.role_name}</div>
                  <div style={{ fontSize:11, color:'#9CA3AF', marginTop:2 }}>{instName} · @{username.trim()}</div>
                </div>
                <button onClick={() => { setStep('username'); setPin(''); setError(''); }}
                  style={{ background:'none', border:'1px solid #E5E7EB', borderRadius:8, padding:'4px 10px', cursor:'pointer', color:'#6B7280', fontSize:11, fontWeight:600 }}>
                  Change
                </button>
              </div>

              <h2 style={{ margin:'0 0 6px', fontWeight:800, color:'#111827', fontSize:20 }}>Enter Your PIN</h2>
              <p style={{ color:'#6B7280', fontSize:13, margin:'0 0 24px' }}>The PIN assigned by your admin</p>

              <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:8 }}>PIN</label>
              <input
                ref={pinRef}
                type="password"
                value={pin}
                onChange={e => setPin(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && verifyPin()}
                placeholder="••••••"
                style={{ ...INP, letterSpacing:8, fontSize:22, textAlign:'center' }}
                autoComplete="current-password"
              />

              <button
                onClick={verifyPin}
                disabled={loading}
                style={{
                  width:'100%', marginTop:16, padding:'13px 0', borderRadius:12, border:'none',
                  background: loading ? '#E5E7EB' : 'linear-gradient(135deg,#10B981,#059669)',
                  color: loading ? '#9CA3AF' : '#fff',
                  fontWeight:800, fontSize:15, cursor: loading ? 'wait' : 'pointer',
                }}>
                {loading ? 'Verifying...' : '✅ Login to Portal'}
              </button>

              <button onClick={() => { setStep('username'); setPin(''); setError(''); }}
                style={{ width:'100%', marginTop:10, padding:'11px 0', borderRadius:12, border:'1.5px solid #E5E7EB', background:'#fff', color:'#374151', fontWeight:600, fontSize:14, cursor:'pointer' }}>
                ← Back
              </button>
            </>
          )}
        </div>

        <div style={{ textAlign:'center', marginTop:20, fontSize:11, color:'#9CA3AF' }}>
          UNIVERSE LMS · Innca Technologies
        </div>
      </div>
    </div>
  );
}